import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { authAxios } from "../utils/auth";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  CreditCard, RefreshCw, CheckCircle, AlertCircle, ExternalLink, HelpCircle,
} from "lucide-react";

/**
 * Payouts panel — Stripe Connect status + resume-onboarding controls.
 *
 * Earnings, per-tier payout preferences, by-tier breakdown and history are
 * intentionally out of scope here. Those live under Commissions (V2 ledger)
 * and Overview → Balance now that the V2 model no longer keys off tiers.
 */
const AffiliatePayoutsPanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const isCyber = currentTheme === "cyber_agent";
  const accentText = isCyber ? "text-cyan-400" : "text-divine-gold";
  const cardBg = isCyber ? "bg-[#0a0a0f] border-cyan-500/20" : "bg-white/5 border-white/10";

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const s = await authAxios.get("/affiliate/stripe-connect/status");
      setStatus(s.data);
    } catch (err) {
      console.error("connect status fetch:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // ── Stripe Connect return handler ──────────────────────────────────────
  // When the user comes back from Stripe onboarding (`/affiliate?return=1`
  // or `?refresh=1`), force a status re-read against the live account and
  // strip the query flag so a page reload doesn't re-trigger it.
  useEffect(() => {
    const returned = searchParams.get("return");
    const refreshed = searchParams.get("refresh");
    if (!returned && !refreshed) return;
    (async () => {
      try {
        const s = await authAxios.get("/affiliate/stripe-connect/status");
        setStatus(s.data);
        if (s.data?.transfers_active || s.data?.charges_enabled) {
          toast.success("Stripe Connect verified — you're ready for cash payouts");
        } else if (s.data?.details_submitted) {
          toast.info("Stripe details received — waiting on Stripe to activate transfers");
        } else {
          toast.warning("Stripe onboarding not yet complete");
        }
      } catch (err) {
        console.error("connect return refresh failed:", err);
        toast.error("Could not refresh Stripe Connect status");
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete("return");
        next.delete("refresh");
        setSearchParams(next, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await authAxios.post("/affiliate/stripe-connect/onboard", {
        origin_url: window.location.origin,
      });
      if (res?.data?.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      } else {
        toast.error("Onboarding link unavailable");
      }
    } catch (err) {
      console.error("connect onboard error:", err);
      toast.error(err.response?.data?.detail || "Could not start Stripe onboarding");
    } finally {
      setConnecting(false);
    }
  };

  const recheckStripeStatus = async () => {
    setCheckingStatus(true);
    try {
      const s = await authAxios.get("/affiliate/stripe-connect/status");
      setStatus(s.data);
      if (s.data?.payouts_enabled) {
        toast.success("Stripe verification complete — bank payouts enabled");
      } else if (s.data?.transfers_active) {
        const due = s.data?.requirements_currently_due?.[0];
        toast.info(
          due
            ? `Stripe is still waiting on: ${due}`
            : "Stripe still verifying — check back shortly"
        );
      } else {
        toast.warning("Stripe onboarding not yet complete");
      }
    } catch (err) {
      console.error("recheck status failed:", err);
      toast.error("Could not refresh Stripe status");
    } finally {
      setCheckingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className={`rounded-xl border ${cardBg} p-6 flex items-center justify-center`}>
        <RefreshCw className={`w-5 h-5 ${accentText} animate-spin`} />
      </div>
    );
  }

  const onboarded = status?.transfers_active === true || status?.charges_enabled === true;
  const payoutsPending = onboarded && status?.payouts_enabled === false;

  return (
    <section data-testid="affiliate-payouts-section" className="space-y-4">
      {payoutsPending && (
        <div
          data-testid="stripe-payouts-pending-banner"
          className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-200 flex flex-wrap items-start gap-3 justify-between"
        >
          <div className="flex-1 min-w-[220px]">
            <div className="font-medium text-amber-100 mb-0.5">
              Stripe verification pending
            </div>
            Transfers to your Stripe balance work, but Stripe won't pay out to your bank
            until verification is complete
            {status?.requirements_currently_due?.length
              ? ` (missing: ${status.requirements_currently_due.slice(0, 3).join(", ")}${
                  status.requirements_currently_due.length > 3 ? "…" : ""
                })`
              : ""}
            .
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={recheckStripeStatus}
              disabled={checkingStatus}
              variant="ghost"
              size="sm"
              className="h-8 text-amber-200 hover:text-amber-100 hover:bg-amber-500/10"
              data-testid="recheck-stripe-status-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${checkingStatus ? "animate-spin" : ""}`} />
              {checkingStatus ? "Checking…" : "Check status"}
            </Button>
            <Button
              onClick={handleConnect}
              disabled={connecting}
              size="sm"
              className="bg-amber-500 hover:bg-amber-600 text-black font-medium whitespace-nowrap h-8"
              data-testid="complete-stripe-verification-btn"
            >
              {connecting ? (
                <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Redirecting…</>
              ) : (
                <><ExternalLink className="w-3.5 h-3.5 mr-1.5" />Complete verification</>
              )}
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className={`text-lg font-semibold ${accentText}`}>Payouts</h2>
      </div>

      {/* Connect Status Card */}
      <div className={`rounded-xl border ${cardBg} p-5`} data-testid="stripe-connect-card">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1 w-full">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className={`w-4 h-4 ${accentText}`} />
              <span className="text-sm text-white/60 uppercase tracking-wider">Stripe Connect</span>
              {onboarded ? (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/30" data-testid="connect-status-pill">
                  <CheckCircle className="w-3 h-3" /> Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-white/5 text-white/60 border border-white/10" data-testid="connect-status-pill">
                  <AlertCircle className="w-3 h-3" /> Not connected
                </span>
              )}
            </div>

            {/* Mobile-only: Stripe Connect actions above the descriptor.
                On desktop these live in the right column below. */}
            <div className="sm:hidden mb-3">
              {!onboarded && (
                <div className="flex items-center gap-3">
                  <Button
                    onClick={handleConnect}
                    disabled={connecting}
                    className="bg-amber-500 hover:bg-amber-600 text-black font-medium whitespace-nowrap"
                    data-testid="connect-stripe-btn-mobile"
                  >
                    {connecting ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Redirecting…</>
                    ) : (
                      <><ExternalLink className="w-4 h-4 mr-2" />Connect Stripe</>
                    )}
                  </Button>
                  <button
                    type="button"
                    onClick={() => navigate("/affiliate/stripe-connect-info")}
                    className="text-amber-500 hover:text-white active:text-white transition-colors"
                    aria-label="How Stripe Connect works"
                    title="How Stripe Connect works"
                    data-testid="stripe-connect-help-icon-mobile"
                  >
                    <HelpCircle className="w-8 h-8" />
                  </button>
                </div>
              )}
              {onboarded && !payoutsPending && (
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="text-[11px] text-white/40 hover:text-white/60 underline decoration-dotted whitespace-nowrap"
                  data-testid="update-stripe-btn-mobile"
                >
                  {connecting ? "Redirecting…" : "Update Stripe details"}
                </button>
              )}
            </div>

            <p className="text-white/70 text-sm">
            {onboarded
                ? "Cash commissions will be transferred to your bank via Stripe."
                : "Use available affiliate balance to purchase coins and subscriptions, or Connect Stripe to receive your affiliate commissions paid into your bank account."}
            </p>
            {!onboarded && (
              <div
                className="mt-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-[12px] text-white/70 leading-relaxed"
                data-testid="stripe-connect-descriptor"
              >
                Create your{" "}
                <em className={`font-bold italic ${accentText}`}>
                  Agent Eros Stripe Wallet
                </em>
                {" "}— the routing account your affiliate payments flow through on
                their way to your bank.{" "}
                <em className={`font-bold italic ${accentText}`}>
                  Link it to an existing Stripe account
                </em>{" "}
                by entering the same email and phone, or{" "}
                <em className={`font-bold italic ${accentText}`}>create a new one</em>.{" "}
                <button
                  type="button"
                  onClick={() => navigate("/affiliate/stripe-connect-info")}
                  className="text-white/60 hover:text-white underline decoration-dotted underline-offset-2 ml-0.5"
                  data-testid="stripe-connect-learn-more-btn"
                >
                  Learn more →
                </button>
              </div>
            )}
          </div>
          {!onboarded && (
            <div className="hidden sm:flex flex-col items-center gap-2">
              <Button
                onClick={handleConnect}
                disabled={connecting}
                className="bg-amber-500 hover:bg-amber-600 text-black font-medium whitespace-nowrap"
                data-testid="connect-stripe-btn"
              >
                {connecting ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Redirecting…</>
                ) : (
                  <><ExternalLink className="w-4 h-4 mr-2" />Connect Stripe</>
                )}
              </Button>
              <button
                type="button"
                onClick={() => navigate("/affiliate/stripe-connect-info")}
                className="text-amber-500 hover:text-white active:text-white transition-colors"
                aria-label="How Stripe Connect works"
                title="How Stripe Connect works"
                data-testid="stripe-connect-help-icon"
              >
                <HelpCircle className="w-8 h-8" />
              </button>
            </div>
          )}
          {onboarded && !payoutsPending && (
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="hidden sm:inline-block text-[11px] text-white/40 hover:text-white/60 underline decoration-dotted whitespace-nowrap"
              data-testid="update-stripe-btn"
            >
              {connecting ? "Redirecting…" : "Update Stripe details"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default AffiliatePayoutsPanel;
